import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable } from '@angular/core';

import { ConditionMatcher } from 'collage-designer/condition-matcher';
import { ConfigService } from 'core/config/config.service';
import { StageConstants } from 'core/constants';
import { CriticalError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';
import { StageUtils } from 'core/utils.stage';
import { closest } from 'fastest-levenshtein';
import { PartConfig, StepConfig, TemplateConfig, TemplateGroupConfig,
         TemplatesSettings
       } from '../collage-designer-config';
import { DesignState, StageConfig } from '../stage/stage-config';

export interface PartLocation {
  designStepId: number;
  part: PartConfig;
}

interface TemplateSelection {
  templateGroupId: string | undefined;
  templateId: string | undefined;
}

export interface AvailableTemplate {
  isDefaultGroup: boolean;
  isDefaultTemplate: boolean;
  tag: string | undefined;
  templateGroupId: string;
  templateId: string;
}

class CriticalTemplateSelectionError extends CriticalError
{
  protected _name: string = 'CriticalTemplateSelectionError';
}

class CriticalStepConfigError extends CriticalError
{
  protected _name: string = 'CriticalStepConfigError';
}

@Injectable()
export class DesignStepService
{
  public static readonly BOX_PART_ID: string = 'box';

  public designStepChanged: EventEmitter<StepConfig> = new EventEmitter();
  public templateChanged: AsyncEventEmitter<TemplateConfig> =
      new AsyncEventEmitter();
  public partChanged: AsyncEventEmitter<PartConfig> = new AsyncEventEmitter();

  private currentDesignStep: DesignState;
  private currentProductId: string;
  private designSteps: Array<DesignState> = [];
  private stepConfigs: Array<StepConfig> = [];
  private previousStepConfig: Array<StepConfig> = [];
  private conditionMatcher: ConditionMatcher;
  private resolvedTemplates: {[url: string]: string} = {};

  constructor(
      @Inject(HttpClient) private http: HttpClient,
      @Inject(ProductService) private productService: ProductService,
      @Inject(ErrorService) private errorService,
      @Inject(ConfigService) private configService: ConfigService,
      @Inject(LoggingService) private loggingService) {}

  public setStepConfigs(productId: string,
                        stepConfigs: Array<StepConfig>):
      void
  {
    this.previousStepConfig = this.stepConfigs;
    this.currentProductId = productId;
    this.stepConfigs = stepConfigs;
  }

  public getDesignStepConfig(designState?: DesignState): StepConfig
  {
    const state = designState || this.currentDesignStep;
    return this.stepConfigs[state.designStepId];
  }

  public async setDesignStep(id: number): Promise<void>
  {
    const possibleDesignSteps = this.designSteps.filter(
        d => d.designStepId === id);
    if (possibleDesignSteps.length === 0) {
      this.currentDesignStep = <DesignState> {
        designStepId: id,
      };
      this.designSteps.push(this.currentDesignStep);
    }
    else {
      this.currentDesignStep = possibleDesignSteps[0];
    }

    this.conditionMatcher = new ConditionMatcher(
        this.configService.getProductDesignerComponentConfig().catalogStoreId,
        this);
    await this.setPartId(
        this.getDesignablePart().id, await this.getTemplateSelection());
    this.designStepChanged.emit(this.stepConfigs[id]);
  }

  private getDesignableParts(designState?: DesignState): Array<PartConfig>
  {
    return this
        .getParts(designState)
        .filter(p => StageConstants
            .DESIGNABLE_PART_IDS.indexOf(p.editingView.type) !== -1);
  }

  public getDesignablePart(designState?: DesignState): PartConfig
  {
    const parts = this.getDesignableParts(designState);

    if (parts.length !== 1) {
      throw new CriticalStepConfigError('multiple designable parts found');
    }

    return parts[0];
  }

  public getDesignStepId(): number
  {
    if (this.currentDesignStep !== undefined) {
      return this.currentDesignStep.designStepId;
    }
    else {
      return 0;
    }
  }

  public getProductId(): string
  {
    return this.currentProductId;
  }

  private findTemplateByPreviousProduct(
      partId: string,
      availableTemplates: Array<AvailableTemplate>
      ): undefined | AvailableTemplate
  {
    const previousParts = Utils.flattenArray(this.previousStepConfig.map(
        s => s.parts.filter(p => p.id === partId)));

    if (previousParts.length === 0) {
      return undefined;
    }

    const previousPartTemplateGroups = previousParts[0].templateGroups.filter(
        g => g.isDefault);
    if (previousPartTemplateGroups.length === 0) {
      return undefined;
    }

    const previousTemplates = Utils
      .objectValuesToArray(previousPartTemplateGroups[0].templates)
      .filter(t => t.isDefault);
    if (previousTemplates.length === 0) {
      return undefined;
    }

    const previousTemplate = previousTemplates[0];
    if (previousTemplate.tag) {
      availableTemplates = availableTemplates.filter(
          t => t.tag === previousTemplate.tag);
    }
    if (availableTemplates.length === 0) {
      return undefined;
    }

    const closestTemplate = closest(
        previousTemplate.id, availableTemplates.map(t => t.templateId));
    return availableTemplates.filter(t => t.templateId === closestTemplate)[0];
  }

  private async findTemplateByPreviousTag(
      availableTemplates: Array<AvailableTemplate>
      ): Promise<undefined | AvailableTemplate>
  {
    const currentDesignStepId = this.currentDesignStep.designStepId;

    const previousDesignStep = Utils.find(
        this.designSteps,
        (ds: DesignState) => ds.designStepId === currentDesignStepId - 1);

    if (! previousDesignStep) {
      return undefined;
    }

    const previousTemplate = await this.getTemplate(previousDesignStep);

    if (! previousTemplate) {
      return undefined;
    }

    const previousTag = previousTemplate.tag;

    if (! previousTag) {
      return undefined;
    }

    return Utils.find(availableTemplates, (t) => t.tag === previousTag);
  }

  private async setPartId(
      partId: string, templateSelection: TemplateSelection): Promise<void>
  {
    this.currentDesignStep.partId = partId;

    const templateGroups = await this.getTemplateGroups();

    const availableTemplates = Utils.flattenArray(
      Utils.byValue(templateGroups)
        .map(g => Utils.byValue(g.templates).map(
          t => (<AvailableTemplate>{
            templateGroupId: g.id,
            templateId: t.id,
            isDefaultGroup: g.isDefault,
            isDefaultTemplate: t.isDefault,
            tag: t.tag,
          }))));

    const firstTemplate = availableTemplates[0];

    const defaultTemplate = Utils.find(
      availableTemplates, t => t.isDefaultTemplate && t.isDefaultGroup);

    const defaultGroup = Utils.find(availableTemplates, t => t.isDefaultGroup);

    const lastUsedTemplate = Utils.find(
      availableTemplates, t =>
        t.templateGroupId === templateSelection.templateGroupId &&
        t.templateId === templateSelection.templateId);

    const previousProductTemplate = this.findTemplateByPreviousProduct(
        partId, availableTemplates);
    const previousStepTagTemplate = await this.findTemplateByPreviousTag(
        availableTemplates);

    const selectedTemplate =
        previousProductTemplate ||
        previousStepTagTemplate ||
        lastUsedTemplate ||
        defaultTemplate ||
        defaultGroup ||
        firstTemplate;

    if (!selectedTemplate) {
      const error = new CriticalTemplateSelectionError(
          'no matching template found', [partId]);
      this.errorService.add(error, 'DesignStepService');
      await this.errorService.flush('DesignStepService');
    }

    await this.setTemplateGroupId(
      selectedTemplate.templateGroupId,
      selectedTemplate.templateId);

    await this.partChanged.emit(this.getPart());
  }

  public getPartId(): string
  {
    return this.currentDesignStep.partId;
  }

  public isBoxStep(): boolean
  {
    return this.currentDesignStep?.partId === DesignStepService.BOX_PART_ID;
  }

  public getParts(designState?: DesignState): Array<PartConfig>
  {
    const state = designState || this.currentDesignStep;

    if (state !== undefined) {
      return this.getDesignStepConfig(state).parts;
    }
    else {
      return [];
    }
  }

  public getPart(designState?: DesignState): PartConfig
  {
    const state = designState || this.currentDesignStep;
    return this.getParts(state).filter(p => p.id === state.partId)[0];
  }

  public async setTemplateGroupId(
      id: string, templateId: string | undefined = undefined): Promise<void>
  {
    this.currentDesignStep.templateGroupId = id;

    for (const templateGroup of await this.getTemplateGroups()) {
      templateGroup.isDefault = false;
      if (templateGroup.id === id) {
        templateGroup.isDefault = true;
      }
    }

    let templates = await this.getTemplates();

    if (! templateId) {
      templateId = Object.keys(templates)[0];
      for (let _templateId of Object.keys(templates)) {
        let template = templates[_templateId];
        if (template.isDefault === true) {
          templateId = template.id;
          break;
        }
      }
    }
    await this.setTemplateId(templateId);
  }

  public async getTemplateGroupId(): Promise<string | undefined>
  {
    if (this.currentDesignStep !== undefined) {
      return this.currentDesignStep.templateGroupId;
    }
    else {
      const group = await this.getTemplateGroups()[0];
      return group ? group.id : undefined;
    }
  }

  public async getTemplateGroups(designState?: DesignState):
      Promise<Array<TemplateGroupConfig>>
  {
    const state = designState || this.currentDesignStep;

    if (! state) {
      return [];
    }

    const templateGroups = this.getPart(state).templateGroups;

    const filteredTemplateGroups = [];
    for (const g of templateGroups) {
      if (await this.conditionMatcher.areConditionsFullfilled(g.conditions)) {
        filteredTemplateGroups.push(g);
      }
    }
    return filteredTemplateGroups;
  }

  private async getTemplateGroup(designState?: DesignState
                                 ): Promise<TemplateGroupConfig>
  {
    const state = designState || this.currentDesignStep;
    return Utils.find(
        await this.getTemplateGroups(state),
        g => g.id === state.templateGroupId);
  }

  public getTemplatesSettings(): TemplatesSettings
  {
    return this.getDesignStepConfig().templatesSettings;
  }

  public async setTemplateId(templateId: string): Promise<void>
  {
    const groups = await this.getTemplateGroup();
    for (let _templateId of Object.keys(groups.templates)) {
      let template = groups.templates[_templateId];
      template.isDefault = false;
      if (template.id === templateId) {
        template.isDefault = true;
      }
    }
    this.currentDesignStep.templateId = templateId;
    await this.templateChanged.emit(await this.getTemplate());
  }

  public async removeTemplate(templateId: string): Promise<void>
  {
    const templateGroup = await this.getTemplateGroup();
    delete templateGroup.templates[templateId];
  }

  public getTemplateId(): string | undefined
  {
    return this.currentDesignStep
        ? this.currentDesignStep.templateId
        : undefined;
  }

  public async getTemplates(designState?: DesignState):
      Promise<{[templateId: string]: TemplateConfig}>
  {
    const state = designState || this.currentDesignStep;

    if (! state) {
      return {};
    }

    return (await this.getTemplateGroup(state)).templates;
  }

  public async getTemplate(designState?: DesignState): Promise<TemplateConfig>
  {
    const state = designState || this.currentDesignStep;
    return (await this.getTemplates(state))[state.templateId];
  }

  public async getTemplateById(templateGroupId: string,
                               templateId: string): Promise<TemplateConfig>
  {
    return Utils
      .find(await this.getTemplateGroups(), g => g.id === templateGroupId)
      .templates[templateId];
  }

  public async setSVGStructure(designState: DesignState, svgStructure: string
                               ): Promise<void>
  {
    (await this.getTemplate(designState)).stageConfig.svgStructure =
        svgStructure;
  }

  public async getSVGStructureUrl(): Promise<string>
  {
    return (await this.getTemplate()).stageConfig.svgStructureURL;
  }

  public getInitialSVGStructureUrl(): string
  {
    return this.currentDesignStep.initialSVGStructureUrl;
  }

  public setInitialSVGStructureUrl(url: string): void
  {
    this.currentDesignStep.initialSVGStructureUrl = url;
  }

  public setDesignedStructureUrl(url: string, designState?: DesignState): void
  {
    const state = designState || this.currentDesignStep;
    state.uploadedStructureUrl = url;
  }

  public getCurrentDesignState(): DesignState
  {
    return this.currentDesignStep;
  }

  public getDesignStates(): Array<DesignState>
  {
    return this.designSteps;
  }

  public getDesignState(stepId: number, partId: string): DesignState
  {
    for (let designState of this.designSteps) {
      if (designState.designStepId === stepId &&
          designState.partId === partId) {
        return designState;
      }
    }
    return undefined;
  }

  private getPartLocationById(id: string): PartLocation
  {
    const parts = Utils.flattenArray(this
        .productService
        .getCurrentProduct()
        .collageDesignerConfig
        .steps
        .map((step: StepConfig, idx: number) =>
            step.parts
                .filter((part: PartConfig) => part.id === id)
                .map((part: PartConfig) => <PartLocation> {
                    designStepId: idx,
                    part: part
                })));

    return parts.shift();
  }

  private getStageConfig(
      part: PartConfig, state: DesignState): StageConfig | undefined
  {
    const templateGroup = Utils.find(
        part.templateGroups, g => g.id === state.templateGroupId);

    if (!templateGroup) {
      return undefined;
    }

    const template = templateGroup.templates[state.templateId];

    if (!template) {
      return undefined;
    }

    return template.stageConfig;
  }

  public async getSVGStructureByPartId(id: string):
      Promise<string | undefined>
  {
    const candidate: PartLocation = this.getPartLocationById(id);

    if (! candidate) {
      return undefined;
    }

    const state = this.getDesignState(
        candidate.designStepId, candidate.part.id);

    if (!state && candidate.part.structureUrl) {
      try {
        return await this.resolveTemplate(candidate.part.structureUrl);
      }
      catch (exception) {
        this.loggingService.log(
            'DesignStepService', Severity.WARNING, exception.toString());
        return undefined;
      }
    }

    if (!state) {
      return undefined;
    }

    const stageConfig = this.getStageConfig(candidate.part, state);
    return stageConfig.svgStructure;
  }

  private async resolveTemplate(svgStructureURL: string): Promise<string>
  {
    if (!this.resolvedTemplates[svgStructureURL]) {
      const resp = <any> await this.http.get(svgStructureURL,
          { observe: 'response', responseType: 'text' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .catch((exc) => {
          throw exc;
        });
      this.resolvedTemplates[svgStructureURL] =
          StageUtils.ensureHasPapNamespace(resp.body);
    }

    return this.resolvedTemplates[svgStructureURL];
  }

  public getPartConfigByPartId(id: string): PartConfig
  {
    return this.getPartLocationById(id).part;
  }

  public getStageRotationByPartId(id: string): number | undefined
  {
    const candidate: PartLocation = this.getPartLocationById(id);

    if (! candidate) {
      return undefined;
    }

    const state = this.getDesignState(
        candidate.designStepId, candidate.part.id);

    if (!state) {
      return undefined;
    }

    const stageConfig = this.getStageConfig(candidate.part, state);
    return stageConfig.rotation;
  }

  private async getTemplateSelection(): Promise<TemplateSelection>
  {
    return {
      templateGroupId: await this.getTemplateGroupId(),
      templateId: this.getTemplateId(),
    };
  }

  public getTemplateIdByStepId(stepId: number): string | undefined
  {
    return this.designSteps.find(
        step => step.designStepId === stepId)?.templateId;
  }
}
